import { getDeliveryNotePdfUrl } from '@/api/consignments';
import { type Rental as ApiRental, type RentalItem as ApiRentalItem } from '@/api/rentals';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useConsignmentsForRental } from '@/hooks/use-consignments';
import { useRentals, useSettlementPreview } from '@/hooks/use-rentals';
import { useReturnAccessories } from '@/hooks/use-return-accessories';
import { useToast } from '@/hooks/use-toast';
import { formatDateSmart, sanitizeCsvValue } from '@/lib/utils';
import { useTranslation, type TranslationParams } from '@kgc/i18n';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// page5 (#421) return-list seam — FE-safe dto sub-path
import type { RentalDueFilter } from '@kgc/rental-core/rental-list';
import { HandoverPhotoUploader } from './components/HandoverPhotoUploader';
import { HandoverReturnPhotoCompare } from './components/HandoverReturnPhotoCompare';
import { ReturnSignSection } from './components/ReturnSignSection';
import { SettlementPreviewPanel } from './components/SettlementPreviewPanel';
import { formatCurrency } from './mock-data';
import {
  buildReturnSettleFields,
  buildSettlementItems,
  calculateDepositRefund,
  calculateEarlyReturn,
  getDaysOverdue,
  getLateFeeBreakdownView,
  getRentalItems,
  initReturnItems,
  isMultiItemRental,
  sanitizeDamageCharge,
  type ReturnItemData,
  type SettlementExtras,
} from './rental-utils';
import {
  ConsignmentSettlementSection,
  buildConsignmentReturnItems,
  type AccessoryCheckItem,
  type ConsignmentReturnItem,
} from './steps/ConsignmentSettlementSection';
import type { CartPricing, Rental, RentalPricing } from './types';
import {
  CONDITION_LABEL_KEYS,
  EquipmentCategory,
  EquipmentCondition,
  EquipmentStatus,
  PricingTier,
  RENTAL_STATUS_LABEL_KEYS,
  RentalStatus,
} from './types';

type ReturnStep = 'select' | 'inspect' | 'payment' | 'confirm';

function getRentalEquipmentLabel(
  rental: Rental,
  t: (key: string, params?: TranslationParams) => string
): string {
  if (rental.items && rental.items.length > 0) {
    if (rental.items.length === 1) {
      return rental.items[0]?.equipment.name ?? t('rental.unknownEquipment');
    }
    return t('rental.equipmentCount', { count: rental.items.length });
  }
  return rental.equipment?.name ?? t('rental.unknownEquipment');
}

function getRentalTotalAmount(pricing: RentalPricing | CartPricing): number {
  if ('grandTotal' in pricing) return pricing.grandTotal;
  return pricing.totalAmount;
}

function mapApiItemToLocal(item: ApiRentalItem): import('./types').RentalItem {
  return {
    id: item.id,
    equipmentId: item.equipmentId,
    equipment: {
      id: item.equipmentId,
      name: item.equipmentName,
      serialNumber: item.serialNumber,
      category: EquipmentCategory.POWER_TOOL,
      status: EquipmentStatus.RENTED,
      condition: EquipmentCondition.GOOD,
      dailyRate: item.dailyRate,
      weeklyRate: item.dailyRate * 6,
      monthlyRate: item.dailyRate * 25,
      depositAmount: item.depositAmount,
    },
    quantity: item.quantity,
    dailyRate: item.dailyRate,
    appliedRate: item.appliedRate,
    itemTotal: item.itemTotal,
  };
}

function mapApiRentalToLocal(r: ApiRental): Rental {
  // Map items array from API
  const items = r.items.length > 0 ? r.items.map(mapApiItemToLocal) : undefined;

  return {
    id: r.id,
    contractNumber: r.rentalCode,
    status: r.status as unknown as RentalStatus,
    partner: {
      id: r.customerId,
      name: r.customerName,
      email: undefined,
      phone: undefined,
      type: 'INDIVIDUAL' as const,
      isVip: false,
      creditLimit: 0,
      balance: 0,
    },
    // For multi-item: equipment is the first item; for single-item: fallback
    equipment: items?.[0]?.equipment ?? {
      id: r.equipmentId,
      name: r.equipmentName,
      serialNumber: '',
      category: EquipmentCategory.POWER_TOOL,
      status: EquipmentStatus.RENTED,
      condition: EquipmentCondition.GOOD,
      dailyRate: r.dailyRate,
      weeklyRate: r.dailyRate * 6,
      monthlyRate: r.dailyRate * 25,
      depositAmount: r.depositAmount,
    },
    items,
    startDate: r.startDate,
    endDate: r.expectedReturnDate,
    depositPaid: r.depositPaid,
    lateFee: r.lateFeeAmount,
    damageCharge: 0,
    rentalPaid: r.totalAmount,
    pricing: {
      tier: PricingTier.DAILY,
      dailyRate: r.dailyRate,
      durationDays: 0,
      grossAmount: r.totalAmount,
      discountAmount: 0,
      netAmount: r.totalAmount,
      vatRate: 0.27,
      vatAmount: 0,
      totalAmount: r.totalAmount,
      depositAmount: r.depositAmount,
    },
    createdAt: r.createdAt,
    createdBy: '',
  };
}

/**
 * page5 (#421): a szűrt visszavétel-lista CSV-tartalma (HU Excel: ';' + BOM).
 * Pure function — a spec közvetlenül asszertálja, Blob nélkül.
 */
export function buildReturnListCsv(rentals: Rental[], t: (key: string) => string): string {
  const header = [
    t('rental.csvHeader.contractNumber'),
    t('rental.csvHeader.partner'),
    t('rental.csvHeader.equipment'),
    t('rental.csvHeader.endDate'),
    t('rental.csvHeader.status'),
  ].join(';');
  const rows = rentals.map(r => {
    const items = getRentalItems(r)
      .map(i => i.equipment.name ?? '')
      .join(' | ');
    return [r.contractNumber, r.partner.name, items, r.endDate ?? '', r.status]
      .map(cell => `"${sanitizeCsvValue(String(cell)).replace(/"/g, '""')}"`)
      .join(';');
  });
  return '\ufeff' + [header, ...rows].join('\n');
}

export function RentalReturnPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedId = searchParams.get('id');

  // No status filter - fetch all rentals, client-side filters for ACTIVE/OVERDUE/PARTIALLY_RETURNED
  // page5 (#421): szerver-oldali due-ablak chip-szűrő (undefined = Mind)
  const [dueFilter, setDueFilter] = useState<RentalDueFilter | undefined>(undefined);
  const {
    rentals: apiRentals,
    loading: rentalsLoading,
    error: rentalsError,
  } = useRentals(dueFilter ? { dueFilter } : {});

  const [step, setStep] = useState<ReturnStep>(preselectedId ? 'inspect' : 'select');
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);

  const [returnItems, setReturnItems] = useState<Map<string, ReturnItemData>>(() => {
    if (!selectedRental) return new Map();
    return initReturnItems(selectedRental);
  });

  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'transfer'>('cash');
  const [notes, setNotes] = useState('');

  // Consignment state for package-based rental return
  const { consignments } = useConsignmentsForRental(selectedRental?.id);
  const [consignmentReturnItems, setConsignmentReturnItems] = useState<ConsignmentReturnItem[]>([]);
  const [accessoryChecklist, setAccessoryChecklist] = useState<AccessoryCheckItem[]>([]);

  // D3 (#491): a tartozék-checklist READ-forrása a return-accessories endpoint
  // (status:null render-start → operátor-toggle). Az üres-start guard ellen (M-015):
  // ha a BE üres listát ad, a checklist üres-state-et mutat (nem néma héj).
  const { accessories: returnAccessories } = useReturnAccessories(selectedRental?.id);
  useEffect(() => {
    setAccessoryChecklist(
      returnAccessories.map(a => ({
        id: a.accessoryId,
        name: a.name,
        quantity: 1,
        // render-start status=null → present-default; az operátor a hiányzókat kikapcsolja
        isReturned: a.status !== 'MISSING',
      }))
    );
  }, [returnAccessories]);

  // Initialize consignment return items when consignment data loads
  // Backend may return nested Consignment[] (with .items) or flat ConsignmentEntity[] records
  useEffect(() => {
    if (consignments.length > 0) {
      const first = consignments[0];
      if (first && 'items' in first && Array.isArray(first.items) && first.items.length > 0) {
        // Nested format: Consignment[] with .items array
        const allItems = consignments.flatMap(c => c.items ?? []);
        setConsignmentReturnItems(buildConsignmentReturnItems(allItems));
      } else {
        // Flat format from backend: each record is a consignment entity
        // Map fields to ConsignmentReturnItem shape
        const raw = consignments as unknown as Array<Record<string, unknown>>;
        const mapped: ConsignmentReturnItem[] = raw
          .filter(c => c['status'] === 'ACTIVE')
          .map(c => ({
            consignmentItemId: String(c['id'] ?? ''),
            productName: String(c['productName'] ?? c['productId'] ?? ''),
            productSku: String(c['productSku'] ?? ''),
            quantityOut: Number(c['quantityOut'] ?? 0),
            quantityReturned: Number(c['quantityOut'] ?? 0), // Default: return all
            unitPrice: Number(c['unitPriceAtOut'] ?? c['unitPrice'] ?? 0),
          }));
        setConsignmentReturnItems(mapped);
      }
    }
  }, [consignments]);

  const hasConsignments = consignmentReturnItems.length > 0;

  // Consignment charges are computed inside ConsignmentSettlementSection
  // but we need them here for the confirm step summary display
  const consignmentChargesTotal = useMemo(() => {
    if (consignmentReturnItems.length === 0) return 0;
    return consignmentReturnItems.reduce((sum, item) => {
      const charged = Math.max(0, item.quantityOut - item.quantityReturned);
      return sum + charged * item.unitPrice;
    }, 0);
  }, [consignmentReturnItems]);

  const handleConsignmentQtyChange = (itemId: string, quantityReturned: number) => {
    setConsignmentReturnItems(prev =>
      prev.map(item => (item.consignmentItemId === itemId ? { ...item, quantityReturned } : item))
    );
  };

  const handleAccessoryToggle = (id: string) => {
    setAccessoryChecklist(prev =>
      prev.map(acc => (acc.id === id ? { ...acc, isReturned: !acc.isReturned } : acc))
    );
  };

  // Computed values
  const returningItems = useMemo(
    () => Array.from(returnItems.values()).filter(i => i.isReturning),
    [returnItems]
  );

  // 40-kör FE-1: read-only settlement-előnézet a pultos inputjaira (#744 pin).
  // Csak a payment-lépésen hív; endpoint-hiba / wire-drift → preview=null → nincs panel.
  // A tartozék-hiány / tisztítás-override extrák per-gép inputok (a #744 request-mezői);
  // a tényleges terhelést a settlement-write (elszámolás-modal darab) viszi majd.
  const [settlementExtras, setSettlementExtras] = useState<Map<string, SettlementExtras>>(
    new Map()
  );
  const updateSettlementExtra = (
    rentalItemId: string,
    field: keyof SettlementExtras,
    value: string
  ) => {
    setSettlementExtras(prev => {
      const next = new Map(prev);
      const sanitized = value === '' ? undefined : sanitizeDamageCharge(value);
      next.set(rentalItemId, { ...next.get(rentalItemId), [field]: sanitized });
      return next;
    });
  };
  const settlementItems = useMemo(
    () => buildSettlementItems(returningItems, settlementExtras),
    [returningItems, settlementExtras]
  );
  const { preview: settlementPreview } = useSettlementPreview(
    selectedRental?.id,
    settlementItems,
    step === 'payment'
  );
  // 40-kör W1 (Javo-ACK 2026-07-02): gépenkénti elszámolás-mód. Default NOW (ratify).
  // Záró-gépnél (nem-részleges visszavétel) a halasztás fel sem ajánlható — a BE
  // zárás-invariánsa úgyis atomian rendez; a UI nem ígér olyat, amit a BE felülír.
  // R-2: forceImmediateSettle bármely deposit-on → halasztás tiltva (preAuth-expiry).
  const [settleMode, setSettleMode] = useState<'NOW' | 'AT_CLOSE'>('NOW');
  const settleForceImmediate = useMemo(
    () => (settlementPreview?.deposits ?? []).some(d => d.forceImmediateSettle),
    [settlementPreview]
  );
  const continuingItems = useMemo(
    () => Array.from(returnItems.values()).filter(i => !i.isReturning),
    [returnItems]
  );
  const isPartialReturn = continuingItems.length > 0 && returningItems.length > 0;
  const isMultiItem = selectedRental ? isMultiItemRental(selectedRental) : false;

  const depositRefund = useMemo(() => {
    if (!selectedRental) return 0;
    return calculateDepositRefund(returnItems, selectedRental.depositPaid, selectedRental.lateFee);
  }, [returnItems, selectedRental]);

  const totalDamageCharge = useMemo(() => {
    return returningItems.reduce((sum, item) => sum + item.damageCharge, 0);
  }, [returningItems]);

  const canProceedFromInspect = returningItems.length > 0;

  // Early return detection (Story 46-11)
  const earlyReturnInfo = useMemo(() => {
    if (!selectedRental) return null;
    return calculateEarlyReturn(selectedRental);
  }, [selectedRental]);

  // Map API rentals to local format
  const localRentals = useMemo(() => apiRentals.map(mapApiRentalToLocal), [apiRentals]);

  // Handle preselection: try paginated list first, fallback to direct API call
  useEffect(() => {
    if (!preselectedId || selectedRental) return;

    // Try from already-loaded paginated list
    if (localRentals.length > 0) {
      const found = localRentals.find(r => r.id === preselectedId);
      if (found) {
        setSelectedRental(found);
        setReturnItems(initReturnItems(found));
        return;
      }
    }

    // Not in paginated list and loading is done → fetch directly by ID
    if (!rentalsLoading) {
      (async () => {
        try {
          const { getRentalById } = await import('@/api/rentals');
          const response = await getRentalById(preselectedId);
          const rental = mapApiRentalToLocal(response.data);
          setSelectedRental(rental);
          setReturnItems(initReturnItems(rental));
        } catch {
          // Rental not found — fall back to select step
          setStep('select');
        }
      })();
    }
  }, [preselectedId, localRentals, selectedRental, rentalsLoading]);

  // Filter active rentals
  const activeRentals = useMemo(() => {
    return localRentals
      .filter(
        r =>
          r.status === RentalStatus.ACTIVE ||
          r.status === RentalStatus.OVERDUE ||
          r.status === RentalStatus.PARTIALLY_RETURNED
      )
      .filter(r => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        const items = getRentalItems(r);
        const matchesItem = items.some(
          item =>
            (item.equipment.name ?? '').toLowerCase().includes(search) ||
            (item.equipment.serialNumber ?? '').toLowerCase().includes(search)
        );
        return (
          r.contractNumber.toLowerCase().includes(search) ||
          r.partner.name.toLowerCase().includes(search) ||
          matchesItem
        );
      });
  }, [searchTerm, localRentals]);

  // page5 (#421): a szűrt lista CSV-exportja (HU Excel: ';' + BOM)
  const handleExportCsv = () => {
    const csv = buildReturnListCsv(activeRentals, t);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visszavetel-lista-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const selectRental = (rental: Rental) => {
    setSelectedRental(rental);
    setReturnItems(initReturnItems(rental));
    setStep('inspect');
  };

  const toggleItemReturning = (itemId: string) => {
    setReturnItems(prev => {
      const next = new Map(prev);
      const item = next.get(itemId);
      if (item) {
        next.set(itemId, { ...item, isReturning: !item.isReturning });
      }
      return next;
    });
  };

  const updateItemCondition = (itemId: string, condition: EquipmentCondition) => {
    setReturnItems(prev => {
      const next = new Map(prev);
      const item = next.get(itemId);
      if (item) {
        next.set(itemId, { ...item, returnCondition: condition });
      }
      return next;
    });
  };

  const updateItemDamageDescription = (itemId: string, value: string) => {
    setReturnItems(prev => {
      const next = new Map(prev);
      const item = next.get(itemId);
      if (item) {
        next.set(itemId, { ...item, damageDescription: value });
      }
      return next;
    });
  };

  const updateItemDamageCharge = (itemId: string, value: string) => {
    setReturnItems(prev => {
      const next = new Map(prev);
      const item = next.get(itemId);
      if (item) {
        next.set(itemId, { ...item, damageCharge: sanitizeDamageCharge(value) });
      }
      return next;
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  // D4 (#491): jogi-P0 — a véglegesítés CSAK ügyfél-aláírás (RENTAL_RETURN esign) után engedélyezett.
  const [returnSigned, setReturnSigned] = useState(false);

  // C1-fix (jogi-P0): a per-tartozék WRITE-payload — stabil ref (useMemo), hogy a ReturnSignSection
  // doc-gen-reset effektje CSAK valódi tartalom-változásra fusson, ne minden renderre.
  const returnAccessoriesWrite = useMemo(
    () =>
      accessoryChecklist.map(a => ({
        accessoryId: a.id,
        status: (a.isReturned ? 'OK' : 'MISSING') as 'OK' | 'MISSING' | 'DAMAGED',
      })),
    [accessoryChecklist]
  );

  // C1-fix (jogi-P0 gate-bypass): az aláírás CSAK az aláírt tartalomra érvényes. Bármely
  // return-tartalom változására (checklist / kondíció / notes / másik bérlés) RESET → újra-aláírás
  // kötelező (a „Vissza → módosít → confirm → aláíratlan/eltérő finalize" bypass ellen).
  useEffect(() => {
    setReturnSigned(false);
  }, [accessoryChecklist, notes, returnItems, selectedRental?.id]);
  const [worksheetCreating, setWorksheetCreating] = useState<string | null>(null); // itemId currently being sent to service

  const handleSendToService = async (item: ReturnItemData) => {
    if (!selectedRental) return;
    setWorksheetCreating(item.rentalItemId);
    try {
      const { createWorksheet, WorksheetType, WorksheetPriority } =
        await import('@/api/worksheets');
      const result = await createWorksheet({
        partnerId: selectedRental.partner.id,
        deviceName: item.equipment.name,
        deviceSerialNumber: item.equipment.serialNumber || undefined,
        faultDescription: item.damageDescription || t('rental.damagedServiceDefaultFault'),
        type: WorksheetType.BERLESI,
        priority: WorksheetPriority.SURGOS,
      });
      toast({ title: t('rental.serviceWorksheetCreated', { code: result.data.worksheetNumber }) });
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t('rental.serviceWorksheetError'),
        variant: 'destructive',
      });
    } finally {
      setWorksheetCreating(null);
    }
  };

  const handleComplete = async () => {
    // M2-fix (defense-in-depth): a finalize-gate NEM csak a button-disabled — belső guard is
    // (a jogi-P0 aláírás-invariáns nem kerülhető meg programatikusan).
    if (!selectedRental || isSubmitting || !returnSigned) return;

    setIsSubmitting(true);
    try {
      const { processReturn } = await import('@/api/rentals');

      // Map the first returning item's condition to API format
      const firstItem = returningItems[0];
      const conditionMap: Record<
        string,
        | 'EXCELLENT'
        | 'GOOD'
        | 'FAIR'
        | 'POOR'
        | 'DAMAGED'
        | 'DAMAGED_USABLE'
        | 'DAMAGED_SERVICE'
        | 'INCOMPLETE'
      > = {
        EXCELLENT: 'EXCELLENT',
        GOOD: 'GOOD',
        FAIR: 'FAIR',
        POOR: 'POOR',
        DAMAGED: 'DAMAGED',
        DAMAGED_USABLE: 'DAMAGED_USABLE',
        DAMAGED_SERVICE: 'DAMAGED_SERVICE',
        INCOMPLETE: 'INCOMPLETE',
      };
      const condition = conditionMap[firstItem?.returnCondition ?? 'GOOD'] ?? 'GOOD';

      // Determine deposit action based on damage charges
      const totalDamageCharge = returningItems.reduce((sum, i) => sum + i.damageCharge, 0);
      let depositAction: 'RETURN' | 'RETAIN_PARTIAL' | 'RETAIN_FULL' = 'RETURN';
      if (totalDamageCharge > 0 && totalDamageCharge < selectedRental.depositPaid) {
        depositAction = 'RETAIN_PARTIAL';
      } else if (
        totalDamageCharge >= selectedRental.depositPaid &&
        selectedRental.depositPaid > 0
      ) {
        depositAction = 'RETAIN_FULL';
      }

      // Collect damage notes from all items
      const damageNotes = returningItems
        .filter(i => i.damageDescription)
        .map(i => `${i.equipment.name}: ${i.damageDescription}`)
        .join('; ');

      // W1: settle-mezők a pure helperből (a szemantika a helper-specben fedett).
      const returnResult = await processReturn(selectedRental.id, {
        returnDate: new Date().toISOString(),
        condition,
        damageNotes: damageNotes || undefined,
        depositAction,
        retainedAmount: totalDamageCharge > 0 ? totalDamageCharge : undefined,
        retentionReason: damageNotes || undefined,
        ...buildReturnSettleFields({
          isPartialReturn,
          isMultiItem,
          settleMode,
          forceImmediate: settleForceImmediate,
          returningItems,
        }),
      });
      // 39-kör FE-4: a BE-számolt késedelmi-díj bontása a return-válaszból (#739 wire).
      // safeParse-gate: régi-shape / hiányzó lateFee → null → nincs bontás-sor (#718-guard).
      const lateFeeBreakdown = getLateFeeBreakdownView(returnResult.data?.lateFee);

      // P0-2: Settle consignment items if any exist
      if (consignmentReturnItems.length > 0) {
        try {
          const { settleConsignment } = await import('@/api/consignments');
          await settleConsignment(selectedRental.id, {
            settlements: consignmentReturnItems.map(item => ({
              consignmentId: item.consignmentItemId,
              quantityReturned: item.quantityReturned,
            })),
          });
        } catch (settleErr) {
          // Settlement failure is critical — user must know
          const errMsg =
            settleErr instanceof Error
              ? settleErr.message
              : t('rental.return.consignment.settleFailedFallback');
          toast({
            title: t('rental.consignment.createFailed'),
            description: `${errMsg}\n\n${t('rental.return.consignment.settleFailedDetail')}`,
            variant: 'destructive',
          });
          console.error('Consignment settlement failed:', settleErr);
        }
      }

      const returnedNames = returningItems.map(i => i.equipment.name).join(', ');
      const returnType = isPartialReturn
        ? t('rental.returnTypePartial')
        : t('rental.returnTypeFull');

      const consignmentInfo =
        consignmentChargesTotal > 0
          ? ` | ${t('rental.consignment.consignmentCharges')}: ${formatCurrency(consignmentChargesTotal)}`
          : '';
      // FE-4 díj-transzparencia: »X megkezdett nap × effektív napi tétel = összeg«
      // — minden érték a wire-ből (BE-terhelt), a FE csak formáz (DRY-money).
      const lateFeeInfo = lateFeeBreakdown
        ? ` | ${t('rental.lateFeeConfirmLabel')} ${t('rental.lateFeeBreakdown', {
            days: lateFeeBreakdown.daysOverdue,
            rate: formatCurrency(lateFeeBreakdown.dailyLateFeeRate),
          })} = ${formatCurrency(lateFeeBreakdown.amount)}` +
          (lateFeeBreakdown.isCapped
            ? ` (${t('rental.lateFeeBreakdownCapped', {
                max: formatCurrency(lateFeeBreakdown.amount),
              })})`
            : '') +
          (lateFeeBreakdown.isLegacyPercentageModel ? ` (${t('rental.lateFeeModelLegacy')})` : '') +
          (lateFeeBreakdown.isWaived ? ` — ${t('rental.lateFeeWaived')}` : '')
        : '';
      toast({
        title:
          t('rental.returnSuccess', {
            type: returnType,
            contract: selectedRental.contractNumber,
            equipment: returnedNames,
            deposit: formatCurrency(depositRefund),
          }) +
          consignmentInfo +
          lateFeeInfo,
      });
      navigate('/rental');
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t('rental.return.error.generic'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: RentalStatus) => {
    switch (status) {
      case RentalStatus.ACTIVE:
        return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
      case RentalStatus.OVERDUE:
        return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
      case RentalStatus.PARTIALLY_RETURNED:
        return 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300';
      default:
        return 'bg-gray-100 dark:bg-[#1f5f6b] text-gray-800 dark:text-[#ccfbf1]';
    }
  };

  const stepLabels: Record<ReturnStep, string> = {
    select: t('rental.selectEquipmentReturn'),
    inspect: t('rental.returnConditionLabel'),
    payment: t('rental.financialSettlement'),
    confirm: t('rental.returnConfirmTitle'),
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f3d47]">
      {/* Header */}
      <header className="bg-white dark:bg-[#1a4f5c] shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/rental')}>
              &larr; {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('rental.rentalReturn')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">{stepLabels[step]}</p>
            </div>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2">
            {(['select', 'inspect', 'payment', 'confirm'] as const).map((s, i) => {
              const stepOrder = ['select', 'inspect', 'payment', 'confirm'] as const;
              const currentIdx = stepOrder.indexOf(step);
              return (
                <div
                  key={s}
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                    step === s
                      ? 'bg-primary text-white'
                      : currentIdx > i
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-[#1f5f6b] text-gray-500 dark:text-[#ccfbf1]'
                  }`}
                >
                  {i + 1}
                </div>
              );
            })}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Step 1: Select rental */}
        {step === 'select' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('common.activeRentals')} ({activeRentals.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  placeholder={t('rental.searchPlaceholder')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="mb-4"
                />

                {/* page5 (#421): due-chip sor (szerver-oldali dueFilter) + találat + CSV */}
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('rental.dueChips.label')}
                  </span>
                  {(
                    [
                      {
                        key: undefined,
                        id: 'all',
                        labelKey: 'rental.dueChips.all',
                        dot: 'bg-gray-400',
                      },
                      {
                        key: 'OVERDUE',
                        id: 'OVERDUE',
                        labelKey: 'rental.dueChips.overdue',
                        dot: 'bg-red-600',
                      },
                      {
                        key: 'DUE_TODAY',
                        id: 'DUE_TODAY',
                        labelKey: 'rental.dueChips.dueToday',
                        dot: 'bg-amber-600',
                      },
                      {
                        key: 'DUE_SOON',
                        id: 'DUE_SOON',
                        labelKey: 'rental.dueChips.dueSoon',
                        dot: 'bg-yellow-500',
                      },
                    ] as const
                  ).map(chip => (
                    <button
                      key={chip.id}
                      type="button"
                      data-testid={`due-chip-${chip.id}`}
                      aria-pressed={dueFilter === chip.key}
                      onClick={() => setDueFilter(chip.key as RentalDueFilter | undefined)}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        dueFilter === chip.key
                          ? 'border-primary bg-primary text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-[#2d7d8a] dark:bg-transparent dark:text-[#ccfbf1]'
                      }`}
                    >
                      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${chip.dot}`} />
                      {t(chip.labelKey)}
                    </button>
                  ))}
                  <div className="flex-1" />
                  <span data-testid="return-list-count" className="text-xs text-muted-foreground">
                    {activeRentals.length} {t('rental.foundLabel')}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="export-csv-btn"
                    onClick={handleExportCsv}
                  >
                    {t('rental.exportCsv')}
                  </Button>
                </div>

                {rentalsLoading && (
                  <div className="py-8 text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-[#ccfbf1]">
                      {t('rental.loadingActive')}
                    </p>
                  </div>
                )}
                {rentalsError && (
                  <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                    <p className="text-sm text-red-800 dark:text-red-300">
                      {t('rental.errorPrefix', { message: rentalsError })}
                    </p>
                  </div>
                )}

                {activeRentals.length === 0 ? (
                  <div className="py-8 text-center text-gray-500 dark:text-[#ccfbf1]">
                    {t('page.noResults')}
                  </div>
                ) : (
                  <>
                    {/* Mobile: card list */}
                    <div className="space-y-3 lg:hidden">
                      {activeRentals.map(rental => {
                        const items = getRentalItems(rental);
                        return (
                          <button
                            key={rental.id}
                            type="button"
                            onClick={() => selectRental(rental)}
                            aria-label={`${rental.contractNumber} - ${rental.partner.name} - ${getRentalEquipmentLabel(rental, t)}`}
                            className={`w-full cursor-pointer rounded-lg border dark:border-[#2d7d8a] p-4 text-left transition-colors hover:border-primary hover:bg-primary/5 ${
                              rental.status === RentalStatus.OVERDUE
                                ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                                : ''
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="mb-1 flex items-center gap-2">
                                  <span className="font-bold text-primary">
                                    {rental.contractNumber}
                                  </span>
                                  <span
                                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(rental.status)}`}
                                  >
                                    {t(RENTAL_STATUS_LABEL_KEYS[rental.status] ?? rental.status)}
                                  </span>
                                  {rental.status === RentalStatus.OVERDUE && (
                                    <span className="text-xs font-medium text-red-600">
                                      {getDaysOverdue(rental)} {t('rental.daysLateLabel')}
                                    </span>
                                  )}
                                  {items.length > 1 && (
                                    <span className="rounded-full bg-blue-100 dark:bg-blue-900/20 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
                                      {t('rental.equipmentCount', { count: items.length })}
                                    </span>
                                  )}
                                </div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {getRentalEquipmentLabel(rental, t)}
                                </p>
                                {items.length <= 1 && rental.equipment && (
                                  <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                                    {rental.equipment.serialNumber}
                                  </p>
                                )}
                                {items.length > 1 && (
                                  <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                                    {items.map(i => i.equipment.name).join(', ')}
                                  </p>
                                )}
                                <p className="mt-1 text-sm text-gray-600 dark:text-[#ccfbf1]">
                                  {t('rental.partnerLabel')}{' '}
                                  <span className="font-medium">{rental.partner.name}</span>
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                                  {formatDateSmart(rental.startDate)} -{' '}
                                  {formatDateSmart(rental.endDate)}
                                </p>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {t('rental.depositLabel')} {formatCurrency(rental.depositPaid)}
                                </p>
                                {rental.lateFee > 0 && (
                                  <p className="text-sm font-medium text-red-600">
                                    {t('rental.lateFeeAmount')} {formatCurrency(rental.lateFee)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Desktop: table view (rental/05-return-list v3-parity) */}
                    <div className="hidden overflow-x-auto lg:block">
                      <table className="w-full text-sm" data-testid="return-rental-table">
                        <thead>
                          <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-[#2d7d8a] dark:text-[#ccfbf1]">
                            <th className="px-3 py-2 font-semibold">
                              {t('rental.returnTable.contract')}
                            </th>
                            <th className="px-3 py-2 font-semibold">
                              {t('rental.returnTable.renter')}
                            </th>
                            <th className="px-3 py-2 font-semibold">
                              {t('rental.returnTable.equipment')}
                            </th>
                            <th className="px-3 py-2 font-semibold">
                              {t('rental.returnTable.expectedReturn')}
                            </th>
                            <th className="px-3 py-2 text-right font-semibold">
                              {t('rental.returnTable.value')}
                            </th>
                            <th className="px-3 py-2 text-center font-semibold">
                              {t('rental.returnTable.status')}
                            </th>
                            <th className="w-10 px-3 py-2">
                              <span className="sr-only">{t('common.actions')}</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeRentals.map(rental => {
                            const items = getRentalItems(rental);
                            const isOverdue = rental.status === RentalStatus.OVERDUE;
                            return (
                              <tr
                                key={rental.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => selectRental(rental)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    selectRental(rental);
                                  }
                                }}
                                aria-label={`${rental.contractNumber} - ${rental.partner.name} - ${getRentalEquipmentLabel(rental, t)}`}
                                data-testid={`return-row-${rental.id}`}
                                className={`cursor-pointer border-b border-gray-100 transition-colors hover:bg-primary/5 dark:border-[#2d7d8a] ${
                                  isOverdue ? 'bg-red-50 dark:bg-red-900/20' : ''
                                }`}
                              >
                                <td className="px-3 py-2">
                                  <span className="font-bold text-primary">
                                    {rental.contractNumber}
                                  </span>
                                  <span className="block text-xs text-gray-500 dark:text-[#ccfbf1]">
                                    {formatDateSmart(rental.startDate)}
                                  </span>
                                </td>
                                <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                                  {rental.partner.name}
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-[#ccfbf1]">
                                  <span className="inline-flex items-center gap-2">
                                    <span>{getRentalEquipmentLabel(rental, t)}</span>
                                    {items.length > 1 && (
                                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                                        {t('rental.equipmentCount', { count: items.length })}
                                      </span>
                                    )}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-700 dark:text-[#ccfbf1]">
                                  {formatDateSmart(rental.endDate)}
                                  {isOverdue && (
                                    <span className="block text-xs font-medium text-red-600">
                                      {getDaysOverdue(rental)} {t('rental.daysLateLabel')}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {formatCurrency(rental.depositPaid)}
                                  </span>
                                  {rental.lateFee > 0 && (
                                    <span className="block text-xs font-medium text-red-600">
                                      {t('rental.lateFeeAmount')} {formatCurrency(rental.lateFee)}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span
                                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(rental.status)}`}
                                  >
                                    {t(RENTAL_STATUS_LABEL_KEYS[rental.status] ?? rental.status)}
                                  </span>
                                </td>
                                <td
                                  className="px-3 py-2 text-right text-gray-400"
                                  aria-hidden="true"
                                >
                                  ›
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loading state: preselected rental not yet loaded */}
        {step === 'inspect' && !selectedRental && (
          <div className="flex flex-col items-center justify-center py-20">
            {rentalsLoading ? (
              <>
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
                <p className="mt-4 text-sm text-muted-foreground">{t('rental.loadingActive')}</p>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">{t('rental.returnNotFound')}</p>
                <Button variant="outline" className="mt-4" onClick={() => setStep('select')}>
                  {t('rental.selectEquipmentReturn')}
                </Button>
              </>
            )}
          </div>
        )}

        {/* Step 2: Inspect equipment */}
        {step === 'inspect' && selectedRental && (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              {/* Multi-item partial return info */}
              {isMultiItem && (
                <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    {t('rental.multiItemInfo')}
                  </p>
                </div>
              )}

              {/* FE-D2 — visszavételkori állapot-fotók (RETURN) */}
              <Card>
                <CardContent className="pt-6">
                  <HandoverPhotoUploader rentalId={selectedRental.id} kind="RETURN" />
                </CardContent>
              </Card>

              {/* PV-KAUCIO-001 — kiadás↔visszavétel állapot-fotó összevetés (kaució-elszámolás) */}
              <Card>
                <CardContent className="pt-6">
                  <HandoverReturnPhotoCompare rentalId={selectedRental.id} />
                </CardContent>
              </Card>

              {/* Early return warning (Story 46-11) */}
              {earlyReturnInfo?.isEarlyReturn && (
                <div
                  role="alert"
                  aria-live="assertive"
                  aria-label={`${t('rental.earlyReturnLabel')}: ${earlyReturnInfo.explanation}`}
                  className={`rounded-lg border p-4 ${
                    earlyReturnInfo.discountChanged
                      ? 'border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20'
                  }`}
                  data-testid="early-return-banner"
                >
                  <p
                    className={`text-sm font-medium ${
                      earlyReturnInfo.discountChanged
                        ? 'text-orange-800 dark:text-orange-300'
                        : 'text-yellow-800 dark:text-yellow-300'
                    }`}
                  >
                    {t('rental.earlyReturnDays', {
                      actual: earlyReturnInfo.actualDays,
                      original: earlyReturnInfo.originalDays,
                    })}
                  </p>
                  {earlyReturnInfo.discountChanged && (
                    <div className="mt-2 space-y-1 text-sm text-orange-700 dark:text-orange-300">
                      <p>
                        {t('rental.originalDiscount')}{' '}
                        <span className="font-medium">
                          {earlyReturnInfo.originalDiscountPercent}% (
                          {earlyReturnInfo.originalTierName})
                        </span>
                      </p>
                      <p>
                        {t('rental.actualDiscount')}{' '}
                        <span className="font-medium">
                          {earlyReturnInfo.actualDiscountPercent}% ({earlyReturnInfo.actualTierName}
                          )
                        </span>
                      </p>
                      <p className="mt-1 text-xs">{t('rental.discountChanged')}</p>
                    </div>
                  )}
                  {!earlyReturnInfo.discountChanged && (
                    <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                      {t('rental.discountUnchanged')}
                    </p>
                  )}
                </div>
              )}

              {/* Equipment items list */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    {isMultiItem
                      ? t('rental.selectEquipmentReturn')
                      : t('rental.selectEquipmentSingle')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Array.from(returnItems.values()).map(item => (
                      <div
                        key={item.rentalItemId}
                        data-testid="return-item"
                        className={`rounded-lg border-2 p-4 transition-colors ${
                          item.isReturning
                            ? 'border-primary'
                            : 'border-gray-200 dark:border-[#2d7d8a] bg-gray-50 dark:bg-[#1f5f6b] opacity-60'
                        }`}
                        style={
                          item.isReturning ? { background: 'var(--card-bg-selected)' } : undefined
                        }
                      >
                        {/* Item header with checkbox */}
                        <div className="flex items-start gap-3">
                          {isMultiItem && (
                            <label className="mt-1 flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                checked={item.isReturning}
                                onChange={() => toggleItemReturning(item.rentalItemId)}
                                className="h-5 w-5 rounded border-gray-300 dark:border-[#2d7d8a] text-primary focus:ring-primary"
                              />
                            </label>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {item.equipment.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                              SN: {item.equipment.serialNumber}
                              {item.quantity > 1 &&
                                ` | ${t('rental.quantityLabel')}: ${item.quantity} db`}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                              {t('rental.issuedCondition')}{' '}
                              <span className="font-medium">
                                {t(
                                  CONDITION_LABEL_KEYS[item.equipment.condition] ??
                                    item.equipment.condition
                                )}
                              </span>
                            </p>
                          </div>
                          <div className="text-right text-sm">
                            <p className="text-gray-500 dark:text-[#ccfbf1]">
                              {t('rental.dailyRate')}
                            </p>
                            <p className="font-medium">
                              {formatCurrency(item.equipment.dailyRate)}
                            </p>
                          </div>
                        </div>

                        {/* Per-item condition & damage (only for returning items) */}
                        {item.isReturning && (
                          <div className="mt-4 space-y-3 border-t pt-3">
                            <div>
                              <label className="mb-1 block text-sm font-medium">
                                {t('rental.returnConditionLabel')}
                              </label>
                              <div className="flex gap-2">
                                {Object.values(EquipmentCondition).map(cond => (
                                  <button
                                    key={cond}
                                    type="button"
                                    onClick={() => updateItemCondition(item.rentalItemId, cond)}
                                    className={`flex-1 rounded-lg border-2 px-2 py-1.5 text-center text-sm transition-colors ${
                                      item.returnCondition === cond
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-gray-200 dark:border-[#2d7d8a] hover:border-gray-300 dark:hover:border-[#2d7d8a]'
                                    }`}
                                  >
                                    {t(CONDITION_LABEL_KEYS[cond])}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div>
                              <label className="mb-1 block text-sm font-medium">
                                {t('rental.damageDescription')}
                              </label>
                              <textarea
                                value={item.damageDescription}
                                onChange={e =>
                                  updateItemDamageDescription(item.rentalItemId, e.target.value)
                                }
                                className="w-full rounded-md border p-2 text-sm"
                                rows={2}
                                placeholder={t('rental.damagePlaceholder')}
                                aria-label={`${t('rental.damageDescriptionAria')}: ${item.equipment.name}`}
                              />
                            </div>

                            {item.returnCondition === EquipmentCondition.DAMAGED_SERVICE && (
                              <div className="rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-3">
                                <p className="mb-2 text-sm text-orange-800 dark:text-orange-300">
                                  {t('rental.damagedServiceInfo')}
                                </p>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-orange-400 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30"
                                  disabled={worksheetCreating === item.rentalItemId}
                                  onClick={() => handleSendToService(item)}
                                >
                                  {worksheetCreating === item.rentalItemId
                                    ? t('common.loading')
                                    : t('rental.sendToService')}
                                </Button>
                              </div>
                            )}

                            <div>
                              <label className="mb-1 block text-sm font-medium">
                                {t('rental.damageChargeLabel')}
                              </label>
                              <Input
                                type="number"
                                value={item.damageCharge || ''}
                                onChange={e =>
                                  updateItemDamageCharge(item.rentalItemId, e.target.value)
                                }
                                placeholder="0"
                                className="max-w-[200px]"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Summary sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('rental.rentalData')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('rental.contractLabel')}
                      </span>
                      <span className="font-medium">{selectedRental.contractNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('rental.partnerLabel')}
                      </span>
                      <span className="font-medium">{selectedRental.partner.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('rental.periodLabel')}
                      </span>
                      <span className="font-medium">
                        {formatDateSmart(selectedRental.startDate)} -{' '}
                        {formatDateSmart(selectedRental.endDate)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('rental.rentalFeeLabel')}
                      </span>
                      <span className="font-medium">
                        {formatCurrency(getRentalTotalAmount(selectedRental.pricing))}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('rental.depositPaidLabel')}
                      </span>
                      <span className="font-medium">
                        {formatCurrency(selectedRental.depositPaid)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {isPartialReturn && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    {t('rental.partialReturnInfo', {
                      returning: returningItems.length,
                      continuing: continuingItems.length,
                    })}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('select')} className="flex-1">
                  {t('common.back')}
                </Button>
                <Button
                  onClick={() => setStep('payment')}
                  disabled={!canProceedFromInspect}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 'payment' && selectedRental && (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t('rental.financialSettlement')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* 41-kör FE-1 (R-3 ratify): render-only konszolidáció — a 40-kör additív
                      blokkjai 3 szekcióban (Terhelések → Elszámolás → Kifizetés); minden belső
                      blokk + testid változatlan, 0 viselkedés-változás. */}
                  <div className="space-y-6">
                    <section
                      aria-labelledby="payment-section-charges-h"
                      className="space-y-3"
                      data-testid="payment-section-charges"
                    >
                      <h3
                        id="payment-section-charges-h"
                        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                      >
                        {t('rental.settlement.sectionCharges')}
                      </h3>
                      {/* Per-item damage charges */}
                      {returningItems.some(i => i.damageCharge > 0) && (
                        <div className="space-y-2 rounded-lg border p-4">
                          <h4 className="font-medium">{t('rental.damageBreakdownLabel')}</h4>
                          {returningItems
                            .filter(i => i.damageCharge > 0)
                            .map(item => (
                              <div
                                key={item.rentalItemId}
                                className="flex justify-between text-sm text-red-600"
                              >
                                <span>{item.equipment.name}</span>
                                <span className="font-medium">
                                  -{formatCurrency(item.damageCharge)}
                                </span>
                              </div>
                            ))}
                        </div>
                      )}

                      {/* Charges */}
                      <div className="space-y-3 rounded-lg border p-4">
                        <h4 className="font-medium">{t('rental.deductions')}</h4>
                        {selectedRental.lateFee > 0 && (
                          <div className="flex justify-between text-red-600">
                            <span>
                              {t('rental.lateFeeWithDays', {
                                days: getDaysOverdue(selectedRental),
                              })}
                            </span>
                            <span className="font-medium">
                              -{formatCurrency(selectedRental.lateFee)}
                            </span>
                          </div>
                        )}
                        {totalDamageCharge > 0 && (
                          <div className="flex justify-between text-red-600">
                            <span>{t('rental.totalDamage')}</span>
                            <span className="font-medium">
                              -{formatCurrency(totalDamageCharge)}
                            </span>
                          </div>
                        )}
                        {selectedRental.lateFee === 0 && totalDamageCharge === 0 && (
                          <p className="text-gray-500 dark:text-[#ccfbf1]">
                            {t('rental.noDeductions')}
                          </p>
                        )}
                      </div>

                      {/* 40-kör: tartozék-hiány + tisztítás-override inputok (a #744
                        preview-request mezői; a terhelést a settlement-write viszi majd) */}
                      {returningItems.length > 0 && (
                        <div
                          className="space-y-3 rounded-lg border p-4"
                          data-testid="settlement-extras"
                        >
                          <h4 className="font-medium">{t('rental.settlement.extrasTitle')}</h4>
                          {returningItems.map(item => (
                            <div key={item.rentalItemId} className="space-y-1">
                              <p className="text-sm text-muted-foreground">{item.equipment.name}</p>
                              <div className="grid gap-2 sm:grid-cols-2">
                                <div>
                                  <label
                                    className="text-xs text-muted-foreground"
                                    htmlFor={`missing-acc-${item.rentalItemId}`}
                                  >
                                    {t('rental.settlement.missingAccessoryLabel')}
                                  </label>
                                  <Input
                                    id={`missing-acc-${item.rentalItemId}`}
                                    data-testid={`missing-accessory-input-${item.rentalItemId}`}
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder={t('rental.settlement.missingAccessoryPlaceholder')}
                                    value={
                                      settlementExtras.get(item.rentalItemId)
                                        ?.missingAccessoryCharge ?? ''
                                    }
                                    onChange={e =>
                                      updateSettlementExtra(
                                        item.rentalItemId,
                                        'missingAccessoryCharge',
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                                <div>
                                  <label
                                    className="text-xs text-muted-foreground"
                                    htmlFor={`cleaning-fee-${item.rentalItemId}`}
                                  >
                                    {t('rental.settlement.cleaningOverrideLabel')}
                                  </label>
                                  <Input
                                    id={`cleaning-fee-${item.rentalItemId}`}
                                    data-testid={`cleaning-override-input-${item.rentalItemId}`}
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder={t('rental.settlement.cleaningDefaultHint')}
                                    value={
                                      settlementExtras.get(item.rentalItemId)
                                        ?.cleaningFeeOverride ?? ''
                                    }
                                    onChange={e =>
                                      updateSettlementExtra(
                                        item.rentalItemId,
                                        'cleaningFeeOverride',
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>

                    {(isPartialReturn || settlementPreview) && (
                      <section
                        aria-labelledby="payment-section-settlement-h"
                        className="space-y-3 border-t pt-4"
                        data-testid="payment-section-settlement"
                      >
                        <h3
                          id="payment-section-settlement-h"
                          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {t('rental.settlement.sectionSettlement')}
                        </h3>
                        {/* 40-kör W1: elszámolás-mód választó — CSAK részleges visszavételnél
                        (záró-gépnél a zárás-invariáns miatt fel sem ajánljuk a halasztást) */}
                        {isPartialReturn && (
                          <div
                            className="space-y-2 rounded-lg border p-4"
                            data-testid="settle-mode-selector"
                          >
                            <h4 className="font-medium">
                              {t('rental.settlement.settleModeTitle')}
                            </h4>
                            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="radio"
                                  name="settle-mode"
                                  data-testid="settle-mode-now"
                                  checked={settleMode === 'NOW'}
                                  onChange={() => setSettleMode('NOW')}
                                />
                                {t('rental.settlement.settleNow')}
                              </label>
                              <label
                                className={`flex items-center gap-2 text-sm ${
                                  settleForceImmediate ? 'opacity-50' : ''
                                }`}
                                title={
                                  settleForceImmediate
                                    ? t('rental.settlement.forceImmediateTooltip')
                                    : undefined
                                }
                              >
                                <input
                                  type="radio"
                                  name="settle-mode"
                                  data-testid="settle-mode-at-close"
                                  disabled={settleForceImmediate}
                                  checked={settleMode === 'AT_CLOSE' && !settleForceImmediate}
                                  onChange={() => setSettleMode('AT_CLOSE')}
                                />
                                {t('rental.settlement.settleAtClose')}
                              </label>
                            </div>
                            {settleMode === 'AT_CLOSE' && !settleForceImmediate && (
                              <p className="text-xs text-muted-foreground">
                                {t('rental.settlement.settleAtCloseHint')}
                              </p>
                            )}
                          </div>
                        )}

                        {/* 40-kör FE-1: BE-számolt elszámolás-előnézet (#744) — graceful: preview
                        nélkül (endpoint még nem él / hiba) a blokk nem renderel */}
                        {settlementPreview && (
                          <SettlementPreviewPanel preview={settlementPreview} />
                        )}
                      </section>
                    )}

                    <section
                      aria-labelledby="payment-section-payout-h"
                      className="space-y-3 border-t pt-4"
                      data-testid="payment-section-payout"
                    >
                      <h3
                        id="payment-section-payout-h"
                        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                      >
                        {t('rental.settlement.sectionPayout')}
                      </h3>
                      {/* Summary */}
                      <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>{t('rental.depositPaidSummary')}</span>
                            <span className="font-medium">
                              {formatCurrency(selectedRental.depositPaid)}
                            </span>
                          </div>
                          {(selectedRental.lateFee > 0 || totalDamageCharge > 0) && (
                            <div className="flex justify-between text-red-600">
                              <span>{t('rental.totalDeductionsSummary')}</span>
                              <span className="font-medium">
                                -{formatCurrency(selectedRental.lateFee + totalDamageCharge)}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between border-t pt-2 text-lg font-bold text-green-700 dark:text-green-300">
                            <span>{t('rental.returnableDeposit')}</span>
                            <span>{formatCurrency(depositRefund)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Payment method */}
                      <div>
                        <label className="mb-2 block text-sm font-medium">
                          {t('rental.depositRefundMethod')}
                        </label>
                        <div className="flex gap-3">
                          {(
                            [
                              { value: 'cash', label: t('rental.cashPayment') },
                              { value: 'card', label: t('rental.cardPayment') },
                              { value: 'transfer', label: t('rental.transferPayment') },
                            ] as const
                          ).map(method => (
                            <button
                              key={method.value}
                              type="button"
                              onClick={() => setPaymentMethod(method.value)}
                              className={`flex-1 rounded-lg border-2 p-3 text-center transition-colors ${
                                paymentMethod === method.value
                                  ? 'border-primary bg-primary/10'
                                  : 'border-gray-200 dark:border-[#2d7d8a] hover:border-gray-300 dark:hover:border-[#2d7d8a]'
                              }`}
                            >
                              {method.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="mb-2 block text-sm font-medium">
                          {t('common.notes')}
                        </label>
                        <textarea
                          value={notes}
                          onChange={e => setNotes(e.target.value)}
                          className="w-full rounded-md border p-3"
                          rows={2}
                          placeholder={t('rental.optionalNotePlaceholder')}
                          aria-label={t('rental.returnNoteAria')}
                        />
                      </div>
                    </section>
                  </div>
                </CardContent>
              </Card>

              {/* Consignment Settlement Section (package-based rentals) */}
              {hasConsignments && (
                <>
                  <ConsignmentSettlementSection
                    accessories={accessoryChecklist}
                    onAccessoryToggle={handleAccessoryToggle}
                    consignmentItems={consignmentReturnItems}
                    onConsignmentQuantityChange={handleConsignmentQtyChange}
                    rentalFeeTotal={
                      selectedRental ? getRentalTotalAmount(selectedRental.pricing) : 0
                    }
                    depositPaid={selectedRental?.depositPaid ?? 0}
                    lateFee={selectedRental?.lateFee ?? 0}
                    damageCharges={totalDamageCharge}
                  />
                  {/* Delivery note PDF download */}
                  {consignments.length > 0 && selectedRental && (
                    <DeliveryNoteDownloadCard
                      rentalId={selectedRental.id}
                      consignments={consignments}
                    />
                  )}
                </>
              )}
            </div>

            {/* Summary sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('common.summary')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('rental.returnedEquipmentLabel')}
                      </span>
                      {returningItems.map(item => (
                        <p key={item.rentalItemId} className="font-medium">
                          {item.equipment.name} &mdash;{' '}
                          {t(CONDITION_LABEL_KEYS[item.returnCondition] ?? item.returnCondition)}
                        </p>
                      ))}
                    </div>
                    {continuingItems.length > 0 && (
                      <div>
                        <span className="text-gray-500 dark:text-[#ccfbf1]">
                          {t('rental.rentalContinuesLabel')}
                        </span>
                        {continuingItems.map(item => (
                          <p
                            key={item.rentalItemId}
                            className="font-medium text-blue-700 dark:text-blue-300"
                          >
                            {item.equipment.name}
                          </p>
                        ))}
                      </div>
                    )}
                    <div className="border-t pt-3">
                      <div className="flex justify-between text-lg font-bold text-green-700 dark:text-green-300">
                        <span>{t('rental.returnableLabel')}</span>
                        <span>{formatCurrency(depositRefund)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('inspect')} className="flex-1">
                  {t('common.back')}
                </Button>
                <Button
                  onClick={() => setStep('confirm')}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Confirm */}
        {step === 'confirm' && selectedRental && (
          <div className="mx-auto max-w-2xl">
            <Card>
              <CardHeader>
                <CardTitle className="text-center">
                  {isPartialReturn
                    ? t('rental.partialReturnConfirmTitle')
                    : t('rental.returnConfirmTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Contract info */}
                  <div className="space-y-3 rounded-lg bg-gray-50 dark:bg-[#1f5f6b] p-4">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('rental.contractSummaryLabel')}
                      </span>
                      <span className="font-bold">{selectedRental.contractNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-[#ccfbf1]">
                        {t('rental.partnerSummaryLabel')}
                      </span>
                      <span className="font-medium">{selectedRental.partner.name}</span>
                    </div>
                  </div>

                  {/* Returned items */}
                  <div className="space-y-2 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
                    <h4 className="font-medium text-green-800 dark:text-green-300">
                      {t('rental.returnedEquipmentLabel')}
                    </h4>
                    {returningItems.map(item => (
                      <div key={item.rentalItemId} className="flex justify-between text-sm">
                        <span>
                          {item.equipment.name}
                          {item.quantity > 1 && ` (${item.quantity} db)`}
                        </span>
                        <span className="font-medium">
                          {t(CONDITION_LABEL_KEYS[item.returnCondition] ?? item.returnCondition)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Continuing items */}
                  {continuingItems.length > 0 && (
                    <div className="space-y-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
                      <h4 className="font-medium text-blue-800 dark:text-blue-300">
                        {t('rental.rentalContinuesLabel')}
                      </h4>
                      {continuingItems.map(item => (
                        <div key={item.rentalItemId} className="text-sm">
                          {item.equipment.name}
                          {item.quantity > 1 && ` (${item.quantity} db)`}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Financial summary */}
                  <div className="rounded-lg border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
                    <div className="text-center">
                      <p className="text-gray-600 dark:text-[#ccfbf1]">
                        {t('rental.returnableDeposit2')}
                      </p>
                      <p className="text-3xl font-bold text-green-700 dark:text-green-300">
                        {formatCurrency(depositRefund)}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-[#ccfbf1]">
                        {paymentMethod === 'cash' && t('rental.cashPayment')}
                        {paymentMethod === 'card' && t('rental.cardPayment')}
                        {paymentMethod === 'transfer' && t('rental.transferPayment')}
                      </p>
                    </div>
                  </div>

                  {/* Warnings / Deductions */}
                  {(selectedRental.lateFee > 0 ||
                    totalDamageCharge > 0 ||
                    consignmentChargesTotal > 0) && (
                    <div className="rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-4">
                      <p className="mb-2 font-medium text-orange-800 dark:text-orange-300">
                        {t('rental.deductions')}:
                      </p>
                      {selectedRental.lateFee > 0 && (
                        <p className="text-sm text-orange-700 dark:text-orange-300">
                          {t('rental.lateFeeConfirmLabel')} {formatCurrency(selectedRental.lateFee)}
                        </p>
                      )}
                      {totalDamageCharge > 0 && (
                        <p className="text-sm text-orange-700 dark:text-orange-300">
                          {t('rental.damageConfirmLabel')} {formatCurrency(totalDamageCharge)}
                        </p>
                      )}
                      {consignmentChargesTotal > 0 && (
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                          {t('rental.consignment.consignmentCharges')}:{' '}
                          {formatCurrency(consignmentChargesTotal)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* D4 (#491): jogi-jegyzőkönyv + ügyfél-aláírás (RENTAL_RETURN esign) — a finalize gate-je */}
                  {selectedRental && (
                    <ReturnSignSection
                      rentalId={selectedRental.id}
                      rentalNumber={selectedRental.contractNumber}
                      accessories={returnAccessoriesWrite}
                      conditionNotes={notes}
                      signed={returnSigned}
                      onSigned={() => setReturnSigned(true)}
                    />
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        // C1-fix: a Vissza-lépés invalidálja az aláírást (a tartalom módosulhat).
                        setReturnSigned(false);
                        setStep('payment');
                      }}
                      className="flex-1"
                    >
                      {t('common.back')}
                    </Button>
                    <Button
                      onClick={handleComplete}
                      disabled={isSubmitting || !returnSigned}
                      className="flex-1 bg-green-600 text-white hover:bg-green-700"
                      data-testid="return-finalize-btn"
                    >
                      {isSubmitting ? t('common.processing') : t('rental.finalizeReturn')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * Delivery Note PDF download card.
 * Shows unique delivery note refs and provides download links for each.
 */
function DeliveryNoteDownloadCard({
  rentalId,
  consignments,
}: {
  rentalId: string;
  consignments: Array<{ deliveryNoteRef?: string | null; deliveryNoteNumber?: string | null }>;
}) {
  const { t } = useTranslation();

  // Extract unique delivery note refs
  const deliveryNoteRefs = useMemo(() => {
    const refs = new Set<string>();
    for (const c of consignments) {
      const ref = c.deliveryNoteRef ?? c.deliveryNoteNumber;
      if (ref && typeof ref === 'string') {
        refs.add(ref);
      }
    }
    return Array.from(refs);
  }, [consignments]);

  if (deliveryNoteRefs.length === 0) return null;

  const handleDownload = (ref: string) => {
    const url = getDeliveryNotePdfUrl(rentalId, ref);
    window.open(`/api/v1${url}`, '_blank');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
            SZL
          </span>
          {t('rental.consignment.deliveryNote')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {deliveryNoteRefs.map(ref => (
            <div
              key={ref}
              className="flex items-center justify-between rounded-lg border border-muted p-3"
            >
              <div>
                <p className="text-sm font-medium text-foreground">{ref}</p>
                <p className="text-xs text-muted-foreground">
                  {t('rental.consignment.deliveryNote')}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleDownload(ref)}>
                PDF
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
